import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  InputAdornment,
  Snackbar,
  alpha,
  TablePagination,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import API from '../../api/axios';

// Normalize any Google Maps link (short link, share link, place link) into an
// embeddable URL so it renders inside an iframe. Proper embed URLs are kept as-is.
const toMapEmbedUrl = (url = '') => {
  const trimmed = (url || '').trim();
  if (!trimmed) return '';
  if (trimmed.includes('output=embed') || trimmed.includes('/maps/embed')) return trimmed;

  let m = trimmed.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (!m) m = trimmed.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
  if (!m) m = trimmed.match(/[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (m) return `https://maps.google.com/maps?q=${m[1]},${m[2]}&z=17&output=embed`;

  m = trimmed.match(/[?&]q=([^&]+)/);
  if (m) return `https://maps.google.com/maps?q=${encodeURIComponent(decodeURIComponent(m[1]))}&output=embed`;

  return trimmed;
};

// Escape hatch: if a pasted link can't be turned into an embed (e.g. a short link),
// build an embed from the address so the map always renders.
const toMapEmbedUrlWithAddress = (mapUrl = '', addressFields = '') => {
  const direct = toMapEmbedUrl(mapUrl);
  if (direct.includes('output=embed') || direct.includes('/maps/embed')) return direct;
  const addr = (addressFields || '').trim();
  if (addr) return `https://maps.google.com/maps?q=${encodeURIComponent(addr)}&output=embed`;
  return direct;
};

const ManageLocations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const defaultForm = { name: '', address: '', city: '', country: '', mapEmbedUrl: '', phone: '', email: '', order: 0 };
  const [formData, setFormData] = useState(defaultForm);

  useEffect(() => { fetchLocations(); }, []);

  const fetchLocations = async () => {
    try {
      const { data } = await API.get('/locations');
      setLocations(data);
    } catch {
      setError('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (loc = null) => {
    if (loc) {
      setEditingLocation(loc);
      setFormData({ name: loc.name, address: loc.address, city: loc.city, country: loc.country, mapEmbedUrl: loc.map_embed_url, phone: loc.phone || '', email: loc.email || '', order: loc.order || 0 });
    } else {
      setEditingLocation(null);
      setFormData(defaultForm);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => { setOpenDialog(false); setEditingLocation(null); };
  const handleChange = (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async () => {
    setSaving(true);
    try {
      // Normalize the pasted Google Maps link so it renders in an iframe.
      const fullAddress = [formData.address, formData.city, formData.country].filter(Boolean).join(', ');
      const payload = { ...formData, mapEmbedUrl: toMapEmbedUrlWithAddress(formData.mapEmbedUrl, fullAddress) };
      if (editingLocation) {
        await API.put(`/locations/${editingLocation.id}`, payload);
        setSnackbar({ open: true, message: 'Location updated successfully', severity: 'success' });
      } else {
        await API.post('/locations', payload);
        setSnackbar({ open: true, message: 'Location created successfully', severity: 'success' });
      }
      fetchLocations();
      handleCloseDialog();
    } catch {
      setSnackbar({ open: true, message: 'Failed to save location', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const openDeleteDialog = (id) => { setDeleteId(id); setDeleteDialogOpen(true); };
  const closeDeleteDialog = () => { setDeleteId(null); setDeleteDialogOpen(false); };

  const handleDelete = async () => {
    try {
      await API.delete(`/locations/${deleteId}`);
      setSnackbar({ open: true, message: 'Location deleted', severity: 'success' });
      fetchLocations();
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete location', severity: 'error' });
    } finally {
      closeDeleteDialog();
    }
  };

  const filtered = locations.filter((l) => !search || l.name?.toLowerCase().includes(search.toLowerCase()) || l.city?.toLowerCase().includes(search.toLowerCase()) || l.country?.toLowerCase().includes(search.toLowerCase()));
  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 2, sm: 4 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>Manage Locations</Typography>
          <Typography variant="body2" color="text.secondary">Update branch locations and details</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ borderRadius: 2.5, background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', boxShadow: '0 4px 15px rgba(17,153,142,0.35)', '&:hover': { boxShadow: '0 8px 25px rgba(17,153,142,0.45)' } }}
        >
          Add New Location
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

      <TextField
        size="small"
        placeholder="Search by name, city or country..."
        value={search}
        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
        sx={{ mb: 3, maxWidth: 400 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }}
      />

      <Paper elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8f9fc' }}>
                {['Branch Name', 'City', 'Country', 'Phone', 'Email', 'Order', 'Actions'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary', py: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.length === 0 ? (
                <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>No locations found.</TableCell></TableRow>
              ) : paginated.map((loc) => (
                <TableRow key={loc.id} sx={{ '&:hover': { bgcolor: alpha('#11998e', 0.03) } }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: alpha('#11998e', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <LocationOnIcon sx={{ fontSize: 18, color: '#11998e' }} />
                      </Box>
                      <Typography variant="body2" fontWeight={600}>{loc.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell><Typography variant="body2">{loc.city}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{loc.country}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{loc.phone || '—'}</Typography></TableCell>
                  <TableCell><Typography variant="body2" sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{loc.email || '—'}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{loc.order}</Typography></TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => handleOpenDialog(loc)} sx={{ color: '#667eea', '&:hover': { bgcolor: alpha('#667eea', 0.1) } }}><EditOutlinedIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => openDeleteDialog(loc.id)} sx={{ color: '#ef4444', '&:hover': { bgcolor: alpha('#ef4444', 0.1) } }}><DeleteOutlineIcon fontSize="small" /></IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={filtered.length} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage} rowsPerPageOptions={[10]} sx={{ borderTop: '1px solid rgba(0,0,0,0.06)' }} />
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>{editingLocation ? 'Edit Location' : 'Add New Location'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField fullWidth label="Branch Name" name="name" value={formData.name} onChange={handleChange} required />
            <TextField fullWidth multiline rows={2} label="Address" name="address" value={formData.address} onChange={handleChange} required />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField fullWidth label="City" name="city" value={formData.city} onChange={handleChange} required />
              <TextField fullWidth label="Country" name="country" value={formData.country} onChange={handleChange} required />
            </Box>
            <TextField fullWidth label="Google Maps Link" name="mapEmbedUrl" value={formData.mapEmbedUrl} onChange={handleChange} required helperText="Paste any Google Maps link (embed URL, Share link, place link or maps.app.goo.gl short link). It is converted automatically so it renders in an iframe." />
            {toMapEmbedUrlWithAddress(formData.mapEmbedUrl, [formData.address, formData.city, formData.country].filter(Boolean).join(', ')) && (
              <Box>
                <Box
                  component="iframe"
                  src={toMapEmbedUrlWithAddress(formData.mapEmbedUrl, [formData.address, formData.city, formData.country].filter(Boolean).join(', '))}
                  title="Map preview"
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  sx={{ width: '100%', height: 180, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 2, display: 'block' }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Live preview
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField fullWidth label="Phone" name="phone" value={formData.phone} onChange={handleChange} />
              <TextField fullWidth label="Email" name="email" type="email" value={formData.email} onChange={handleChange} />
            </Box>
            <TextField fullWidth label="Display Order" name="order" type="number" value={formData.order} onChange={handleChange} sx={{ maxWidth: 200 }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={handleCloseDialog} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving} sx={{ borderRadius: 2, background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white', boxShadow: 'none' }}>
            {saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : (editingLocation ? 'Update Location' : 'Create Location')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} PaperProps={{ sx: { borderRadius: 3, maxWidth: 400 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Location</DialogTitle>
        <DialogContent><Typography variant="body2" color="text.secondary">Are you sure you want to delete this location? This action cannot be undone.</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeDeleteDialog} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleDelete} sx={{ borderRadius: 2, bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' }, boxShadow: 'none' }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2 }} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageLocations;